/**
 * 「我嘅貨品」（P1.5-D；供應商自己嘅貨品清單 ＋ 新增）—— 放喺「我嘅資料」頁
 *
 * 為何要（產品負責人 2026-09-28 問「where can i add new item?」）：
 *   之前供應商**只有**喺「出貨 → 新增發貨單」入面「即場新增貨品」加得到；
 *   自己嗰邊完全冇貨品清單 → 想加貨／睇規格都要開單。呢個元件補返個缺口。
 *
 * 紅線
 *   · 貨品一律經 `POST /api/items`（供應商只可以加**自己**嘅貨；後端信 session 唔信 body）
 *   · ⭐ cbm **一律由後端 `@coeldery/sku-schema` 計** —— 呢度只顯示，唔准自己乘長闊高
 *   · 安全庫存（`safe_level`）係 **CoEldery 85 落單用** → 呢度只讀顯示（要改搵職員）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { badge, btnGhost, btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import ItemCreateForm from './ItemCreateForm';
import { ApiError, api, type Item, type Me, type Supplier } from '../lib/api';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-divider)',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' };
const mono: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 };

export default function MyItemsPanel({ user }: { user: Me }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [itm, sup] = await Promise.all([api.items(user.supplierId ?? undefined), api.suppliers()]);
      setItems(itm.items ?? []);
      setSuppliers(sup.suppliers ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, user.supplierId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{t('myItems.title')}</h3>
      <p style={hintStyle}>{t('myItems.hint')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}

      <button type="button" style={showAdd ? btnGhost : btnPrimary} onClick={() => setShowAdd((v) => !v)}>
        {showAdd ? t('myItems.hideAdd') : t('myItems.add')}
      </button>

      {showAdd && user.supplierId && (
        <div style={{ marginTop: '12px', padding: '12px', border: '1px dashed var(--color-divider)', borderRadius: '12px' }}>
          <ItemCreateForm
            suppliers={suppliers}
            lockedSupplierId={user.supplierId}
            onCreated={async () => {
              await load();
              setShowAdd(false);
            }}
          />
        </div>
      )}

      {loading ? (
        <p style={hintStyle}>{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p style={hintStyle}>{t('myItems.empty')}</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: '10px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr>
                <th style={th}>{t('master.itemName')}</th>
                <th style={th}>{t('master.barcode')}</th>
                <th style={th}>{t('myItems.unitsPerCarton')}</th>
                <th style={th}>{t('myItems.cartonSize')}</th>
                <th style={th}>{t('myItems.cbmPerCarton')}</th>
                <th style={th}>{t('myItems.safeLevel')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={td}>
                    {it.name} {it.active ? '' : <span style={badge('off')}>{t('myItems.inactive')}</span>}
                  </td>
                  <td style={td}>{it.barcode}</td>
                  <td style={{ ...td, ...mono }}>{it.unitsPerCarton}</td>
                  <td style={td}>
                    {it.cartonLCm}×{it.cartonWCm}×{it.cartonHCm} cm
                  </td>
                  <td style={{ ...td, ...mono }}>{it.cbmPerCarton}</td>
                  <td style={{ ...td, ...mono }}>{it.safeLevel ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
